import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

export interface RecipeSummary {
  recipeId: string;
  recipeType: string;
  name: string;
  description: string | null;
  level: number;
  requiresLearned: boolean;
  inputs: Array<{ itemName: string; quantity: number }>;
  outputs: Array<{ itemName: string; quantity: number; probability?: number }>;
  constraints: Record<string, unknown>;
  isEnabled: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface UseRecipesOptions {
  type?: string;
  search?: string;
  enabled?: boolean;
  admin?: boolean;
}

interface UseRecipesReturn {
  recipes: RecipeSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useRecipes(options: UseRecipesOptions = {}): UseRecipesReturn {
  const [recipes, setRecipes] = useState<RecipeSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const fetchIdRef = useRef(0);

  const fetchRecipes = useCallback(
    async (newOffset: number = 0, append: boolean = false) => {
      const id = ++fetchIdRef.current;
      try {
        setError(null);
        setLoading(true);
        const params: Record<string, string> = {};
        if (options.type) params.type = options.type;
        if (options.search) params.search = options.search;
        if (options.enabled !== undefined) params.enabled = String(options.enabled);
        params.limit = String(limit);
        params.offset = String(newOffset);

        const path = options.admin ? '/arches/crafting/admin/recipes' : '/arches/crafting/recipes';
        const { data } = await api.get<{ recipes: RecipeSummary[]; total: number }>(path, {
          params,
        });
        if (fetchIdRef.current !== id) return;
        if (append) {
          setRecipes((prev) => [...prev, ...data.recipes]);
        } else {
          setRecipes(data.recipes);
        }
        setTotal(data.total);
        setOffset(newOffset);
      } catch (err) {
        if (fetchIdRef.current !== id) return;
        setError(err instanceof Error ? err.message : 'Failed to load recipes');
      } finally {
        if (fetchIdRef.current === id) {
          setLoading(false);
        }
      }
    },
    [options.type, options.search, options.enabled, options.admin],
  );

  useEffect(() => {
    fetchRecipes(0);
  }, [fetchRecipes]);

  const refresh = useCallback(() => fetchRecipes(0), [fetchRecipes]);
  const loadMore = useCallback(() => {
    if (offset + limit < total) {
      fetchRecipes(offset + limit, true);
    }
  }, [fetchRecipes, offset, total]);

  return {
    recipes,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
  };
}
